import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { onboardingGuard } from './core/auth/onboarding.guard';
import { adminGuard } from './core/auth/role.guard';
import { platformAccessGuard } from './core/auth/platform-access.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'onboarding',
  },
  {
    path: 'offline',
    canActivate: [MsalGuard],
    loadComponent: () => {
      return import('./shared/components/app-offline/app-offline').then((m) => m.AppOffline);
    },
  },
  {
    path: 'no-platform-access',
    loadComponent: () =>
      import('./shared/components/no-platform-access/no-platform-access').then(
        (m) => m.NoPlatformAccess,
      ),
  },
  {
    path: 'onboarding',
    canActivate: [MsalGuard, platformAccessGuard],
    canActivateChild: [MsalGuard],
    loadComponent: () => {
      return import('./core/layout/home/home').then((m) => m.Home);
    },
  },
  {
    path: 'charts',
    // canActivate: [MsalGuard],
    // canActivateChild: [MsalGuard],
    loadComponent: () => {
      return import('./features/charts-try/charts-try').then((m) => m.ChartsTry);
    },
  },
  {
    path: 'dashboard',
    canActivate: [MsalGuard, platformAccessGuard, onboardingGuard],
    canActivateChild: [MsalGuard],
    loadComponent: () =>
      import('./core/layout/dashboard-layout/dashboard-layout.component').then(
        (m) => m.DashboardLayoutComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '',
      },

      {
        path: '',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'conversation',
        loadComponent: () =>
          import('./features/dashboard/dashboard-details/ask-ai/ask-ai.component').then(
            (m) => m.AskAiComponent,
          ),
      },
      {
        path: 'conversation/:id',
        loadComponent: () =>
          import('./features/dashboard/dashboard-details/ask-ai/ask-ai.component').then(
            (m) => m.AskAiComponent,
          ),
      },
      {
        path: 'details',
        loadComponent: () =>
          import('./features/dashboard/dashboard-details/dashboard-details.component').then(
            (m) => m.DashboardDetailsComponent,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'sr/all',
          },
          {
            path: 'sr',
            loadComponent: () =>
              import('./features/dashboard/dashboard-details/sr/sr.component').then(
                (m) => m.SrComponent,
              ),
            loadChildren: () =>
              import('./features/dashboard/dashboard-details/sr/sr.routes').then(
                (m) => m.SR_ROUTES,
              ),
          },
        ],
      },
      {
        path: 'for-you',
        loadComponent: () =>
          import('./features/dashboard/dashboard-for-you-details/dashboard-for-you-details.component').then(
            (m) => m.DashboardForYouDetailsComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile-details/profile-details.component').then(
            (m) => m.ProfileDetails,
          ),
      },
      {
        path: 'spotlight',
        loadComponent: () =>
          import('./features/dashboard/dashboard-spotlight/dashboard-spotlight-details/dashboard-spotlight-details.component').then(
            (m) => m.DashboardSpotlightDetailsComponent,
          ),
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./features/dashboard/dashboard-upcoming-events/dashboard-events-details/dashboard-events-details.component').then(
            (m) => m.DashboardEventsDetailsComponent,
          ),
      },
      {
        path: 'departments',
        loadComponent: () =>
          import('./features/departments/departments.component').then(
            (m) => m.DepartmentsComponent,
          ),
      },
      {
        path: 'people-finder',
        loadComponent: () =>
          import('./features/dashboard/people-finder/people-finder-main/people-finder.component').then(
            (m) => m.PeopleFinderComponent,
          ),
      },
      {
        path: 'mobile-search',
        loadComponent: () =>
          import('./features/dashboard/mobile-search/mobile-search.component').then(
            (m) => m.MobileSearchComponent,
          ),
      },
      {
        path: '**',
        canActivate: [MsalGuard],
        loadComponent: () =>
          import('./features/page-not-found/page-not-found').then((m) => m.PageNotFound),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [MsalGuard, onboardingGuard],
    canActivateChild: [adminGuard],
    loadComponent: () =>
      import('./features/admin/admin-layout/admin-layout.component').then(
        (m) => m.AdminLayoutComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'usage',
      },
      {
        path: 'usage',
        loadComponent: () =>
          import('./features/admin/usage/usage.component').then((m) => m.UsageComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'users',
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./features/admin/usage/usage-users/usage-users.component').then(
                (m) => m.UsageUsersComponent,
              ),
          },
          {
            path: 'feedback',
            loadComponent: () =>
              import('./features/admin/usage/usage-feedback/usage-feedback.component').then(
                (m) => m.UsageFeedbackComponent,
              ),
          },
          {
            path: 'search-categories',
            loadComponent: () =>
              import('./features/admin/usage/usage-search-categories/usage-search-categories.component').then(
                (m) => m.UsageSearchCategoriesComponent,
              ),
          },
          {
            path: 'search-categories/all-prompts',
            loadComponent: () =>
              import('./features/admin/usage/usage-search-categories/usage-all-prompts/usage-all-prompts.component').then(
                (m) => m.UsageAllPromptsComponent,
              ),
          },
          {
            path: 'modules',
            loadComponent: () =>
              import('./features/admin/usage/usage-modules/usage-modules.component').then(
                (m) => m.UsageModulesComponent,
              ),
          },
        ],
      },
      {
        path: 'collections',
        loadComponent: () =>
          import('./features/admin/collections/collections.component').then(
            (m) => m.CollectionsComponent,
          ),
      },
      {
        path: 'preferences',
        loadComponent: () =>
          import('./features/admin/preferences/preferences.component').then(
            (m) => m.PreferencesComponent,
          ),
      },
      {
        path: 'performance',
        loadComponent: () =>
          import('./features/admin/performance/performance.component').then(
            (m) => m.PerformanceComponent,
          ),
      },
      {
        path: '**',
        canActivate: [MsalGuard],
        loadComponent: () =>
          import('./features/page-not-found/page-not-found').then((m) => m.PageNotFound),
      },
    ],
  },
  {
    path: '**',
    canActivate: [MsalGuard],
    loadComponent: () =>
      import('./features/page-not-found/page-not-found').then((m) => m.PageNotFound),
  },
];
